import { useState } from 'react';
import { endOfDay, startOfDay } from 'date-fns';
import { useQuery } from '@apollo/client';
import { CSVLink, Flex, StyledDatePicker, StyledFormSelect } from '../';
import { ALL_SERVICE_PROVIDERS, EXPORT_SERVICE_PROVIDERS } from '../../../graphql';
import { formatSelectValue, getPortal } from '../../../utils';
import { getAllProgrammesQuery } from '../../../containers/shared/programmes/utils';

type Props = {
  programme: string;
  serviceProvider: string;
};

type State = {
  programmeIds?: string[];
  serviceProviderIds?: string[];
};

const headers = [
  { label: 'Programme', key: 'programmes[0].name' },
  { label: 'Service Provider ID	', key: 'id' },
  { label: 'Service Provider', key: 'name' },
  { label: 'Description', key: 'description' },
  { label: 'Type', key: 'type' },
  { label: 'Registration Number', key: 'registrationNumber' },
  { label: 'Physical Address', key: 'physicalAddress' },
  { label: 'Website', key: 'website' },
  { label: 'Contact Number', key: 'contactNumber' },
  { label: 'Organization Leader', key: 'orgLeader' },
  { label: 'Organization Leader Designation', key: 'orgLeaderDesignation' },
  { label: 'Organization Leader Number', key: 'orgLeaderNumber' },
  { label: 'Organization Leader Email', key: 'orgLeaderPersonEmail' },
  { label: 'First Contact Person', key: 'firstContactPerson' },
  { label: 'First Contact Person Designation', key: 'firstContactPersonDesignation' },
  { label: 'First Contact Person Number', key: 'firstContactPersonNumber' },
  { label: 'First Contact Person Email', key: 'firstContactPersonEmail' },
  { label: 'Second Contact Person', key: 'secondContactPerson' },
  { label: 'Second Contact Person Designation', key: 'secondContactPersonDesignation' },
  { label: 'Second Contact Person Number', key: 'secondContactPersonNumber' },
  { label: 'Second Contact Person Email', key: 'secondContactPersonEmail' },
];

const ServiceProviderExport = ({ serviceProvider, programme }: Props) => {
  const [portal] = useState(getPortal());
  const [state, setState] = useState<State>({});
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { data: programmeData, error: programmeError, loading: programmeDataLoading } = useQuery(
    getAllProgrammesQuery(portal),
    {
      variables: {
        input: {
          isArchived: false,
          includeWhereReporter: true,
        },
      },
    }
  );
  const { data: spData, error: spError, loading: spDataLoading } = useQuery(ALL_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });
  const { data: exportData, loading: exportLoading } = useQuery(EXPORT_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
        ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
        ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
      },
    },
  });

  return (
    <Flex size="2" direction="column">
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="programmeIds"
          label="Programme"
          placeholder={programmeDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={programmeDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              [programme]: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            programmeDataLoading || programmeError || !(programmeData && programmeData.programmes)
              ? []
              : programmeData.programmes.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1.2" direction="column">
        <StyledDatePicker
          label="Programme Date"
          name={`programmeDate`}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isMulti={true}
            isClearable={true}
            name="serviceProviderIds"
            label="Service Provider"
            placeholder={spDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
            isDisabled={spDataLoading}
            onChange={(option: any[]) => {
              const newState = Object.assign({}, state, {
                [serviceProvider]: option ? option.map(opt => opt.value) : undefined,
              });
              setState(newState);
            }}
            options={
              spDataLoading || spError || !(spData && spData.serviceProviders)
                ? []
                : spData.serviceProviders.map((item: any) =>
                    formatSelectValue({
                      label: item.name,
                      value: item.id,
                    })
                  )
            }
          />
        </Flex>
      </Flex>
      {exportLoading ? (
        <div>Preparing data for export...</div>
      ) : (
        <Flex size="1" direction="row" marginRight="10px">
          {exportData && exportData.exportServiceProviders && (
            <CSVLink headers={headers} data={exportData.exportServiceProviders}>
              Export CSV
            </CSVLink>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ServiceProviderExport;
